import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../../state/editorStore';
import { api } from '../../api/client';
import { saveEdits } from '../../lib/pipeline';

type Phase = 'idle' | 'exporting' | 'done' | 'error';

/** Clean download glyph (arrow into a tray) as an SVG, so it renders identically
 *  everywhere instead of relying on an inconsistent Unicode font glyph. */
function DownloadIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ verticalAlign: '-2px' }}
    >
      <path d="M12 3v11" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 20h14" />
    </svg>
  );
}

function fmtEta(sec: number | null): string {
  if (sec == null) return 'estimating…';
  if (sec < 60) return `~${sec}s left`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `~${m}m ${s}s left` : `~${m}m left`;
}

/**
 * Export button that renders in the BACKGROUND: it fills with the render % and
 * shows an ETA in place, so the user can keep editing. On completion it triggers
 * the download and offers to grab it again.
 */
export function StudioExportButton() {
  const projectId = useEditor((s) => s.projectId);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [eta, setEta] = useState<number | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  // The last COMPLETED export (for the ⭳ button next to Export). We only keep
  // its finish time here - the download URL is presigned and expires, so we
  // re-fetch a fresh one on click.
  const [lastExport, setLastExport] = useState<{ finishedAt: string | null } | null>(null);
  const poll = useRef<number | null>(null);
  const started = useRef(0);
  const jobId = useRef<string | null>(null);

  useEffect(() => () => { if (poll.current) window.clearInterval(poll.current); }, []);

  const refreshLast = () => {
    if (!projectId) return;
    api
      .getLatestRender(projectId)
      .then((r) => setLastExport(r.url ? { finishedAt: r.finishedAt ?? null } : null))
      .catch(() => undefined);
  };

  const downloadLast = async () => {
    if (!projectId) return;
    try {
      const r = await api.getLatestRender(projectId); // fresh presigned URL
      if (r.url) download(r.url);
    } catch {
      /* ignore - the button just no-ops if the render is gone */
    }
  };

  const download = (u: string) => {
    const a = document.createElement('a');
    a.href = u;
    a.download = '';
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const stop = () => { if (poll.current) window.clearInterval(poll.current); poll.current = null; };

  const start = async () => {
    if (!projectId || phase === 'exporting') return;
    setPhase('exporting');
    setProgress(0);
    setEta(null);
    setUrl(null);
    started.current = Date.now();
    try {
      await saveEdits();
      const { renderJobId } = await api.export(projectId, {
        aspectRatio: useEditor.getState().aspect,
        fit: 'fill',
        resolution: '1080p',
        fps: 30,
        watermark: useEditor.getState().watermark,
      });
      jobId.current = renderJobId;
      stop();
      poll.current = window.setInterval(tick, 1500);
    } catch {
      setPhase('error');
    }
  };

  const tick = async () => {
    if (!projectId || !jobId.current) return;
    try {
      const j = await api.getExport(projectId, jobId.current);
      if (j.status === 'failed') {
        setPhase('error');
        stop();
        return;
      }
      const p = Math.max(0, Math.min(1, j.progress || 0));
      setProgress(p);
      const elapsed = (Date.now() - started.current) / 1000;
      if (p > 0.03) setEta(Math.max(1, Math.round((elapsed * (1 - p)) / p)));
      if (j.status === 'completed') {
        setProgress(1);
        stop();
        const dl = await api.getExportDownload(projectId, jobId.current);
        setUrl(dl.url);
        setPhase('done');
        download(dl.url);
        refreshLast(); // a new "last export" now exists

      }
    } catch {
      /* transient - keep polling */
    }
  };

  // Resume an in-progress render after a refresh: reattach to the latest job
  // and keep filling (ETA from the server's start time).
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    refreshLast();
    api
      .getLatestExport(projectId)
      .then((r) => {
        if (cancelled || !r.job) return;
        const j = r.job;
        if (j.status === 'processing' || j.status === 'queued') {
          jobId.current = j.id;
          started.current = new Date(j.startedAt).getTime();
          setProgress(Math.max(0, Math.min(1, j.progress || 0)));
          setPhase('exporting');
          stop();
          poll.current = window.setInterval(tick, 1500);
        }
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const pct = Math.round(progress * 100);

  const lastTitle = lastExport
    ? `Download last export${lastExport.finishedAt ? ` · ${new Date(lastExport.finishedAt).toLocaleString()}` : ''}`
    : '';

  let mainBtn;
  if (phase === 'exporting') {
    mainBtn = (
      <button className="st-export exporting" disabled aria-label={`Exporting ${pct}%`}>
        <span className="st-export-fill" style={{ width: `${Math.max(6, pct)}%` }} />
        <span className="st-export-label">
          <span className="st-export-spin" /> {pct}% · {fmtEta(eta)}
        </span>
      </button>
    );
  } else if (phase === 'done') {
    mainBtn = (
      <button className="st-export done" onClick={() => url && download(url)} title="Download again">
        ✓ Exported · Download
      </button>
    );
  } else {
    mainBtn = (
      <button className="st-export" onClick={start} disabled={!projectId} title={projectId ? 'Export video' : 'Open a project to export'}>
        {phase === 'error' ? '↻ Retry export' : <><DownloadIcon />&nbsp;Export</>}
      </button>
    );
  }

  return (
    <div className="st-export-group">
      {/* Download the LAST completed export. Hidden until one exists, and while
          the "done" button already offers the just-finished download. */}
      {lastExport && phase !== 'done' && (
        <button className="st-dl" onClick={downloadLast} title={lastTitle} aria-label={lastTitle}>
          <DownloadIcon size={16} />
        </button>
      )}
      {mainBtn}
    </div>
  );
}
